
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Account, 
  LeadAccount, 
  StrategyWithDetails 
} from "@/types/strategy-types";
import AccountsTableHeader from "./AccountsTableHeader";
import EmptyAccountsTable from "./EmptyAccountsTable";
import { AccountsTableProvider } from "./AccountsTableProvider";
import AccountsDialogs from "./AccountsDialogs";
import AccountsDataTable from "./AccountsDataTable";

interface AccountsTableProps {
  accounts: Account[];
  leadAccounts: LeadAccount[];
  strategies: StrategyWithDetails[];
  onCreateAccount: (account: Partial<Account>) => Promise<void>;
  onUpdateAccount: (id: string, account: Partial<Account>) => Promise<void>;
  onDeleteAccount: (id: string) => Promise<void>;
  onToggleActive: (id: string, active: boolean) => Promise<void>;
  userRole?: string | null;
}

const AccountsTable = ({
  accounts,
  leadAccounts,
  strategies,
  onCreateAccount,
  onUpdateAccount,
  onDeleteAccount,
  onToggleActive,
  userRole
}: AccountsTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAccounts = accounts.filter(account => 
    account.external_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.exchange.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  return (
    <AccountsTableProvider>
      <div className="space-y-4">
        <AccountsTableHeader 
          title="Accounts Management" 
          description="Manage accounts connected to strategies"
        />
        
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="w-full md:w-1/3">
            <Input
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full"
            />
          </div>
          
          <Button onClick={() => {
            const event = new CustomEvent('openCreateAccountDialog');
            window.dispatchEvent(event);
          }}>
            Add Account
          </Button>
        </div>
        
        {accounts.length === 0 ? (
          <EmptyAccountsTable />
        ) : (
          <AccountsDataTable 
            accounts={filteredAccounts}
            leadAccounts={leadAccounts}
            strategies={strategies}
            onToggleActive={onToggleActive}
            onEdit={(account) => {
              const event = new CustomEvent('openEditAccountDialog', { detail: { account } });
              window.dispatchEvent(event);
            }}
            onDelete={(account) => {
              const event = new CustomEvent('openDeleteAccountDialog', { detail: { account } });
              window.dispatchEvent(event);
            }}
            userRole={userRole}
          />
        )}
        
        <AccountsDialogs
          leadAccounts={leadAccounts}
          strategies={strategies}
          onCreateAccount={onCreateAccount}
          onUpdateAccount={onUpdateAccount}
          onDeleteAccount={onDeleteAccount}
          userRole={userRole}
        />
      </div>
    </AccountsTableProvider>
  );
};

export default AccountsTable;
